/**
 * rondo-web-forms
 *
 * Rondo Web Forms
 *
 * @author Jiri Hybek <jiri@hybek.cz>
 * @license See LICENSE file distributed with this source code.
 */

import React, { useMemo } from "react";

import { bem } from "../../../shared/BEM";
import { DocumentManager } from "../../lib/DocumentManager";
import { Button } from "./ButtonComponent";
import { MenuSection } from "./MenuSectionComponent";
import { OpenedDocuments } from "./OpenedDocumentsComponent";
import { ButtonGroup } from "./ButtonGroupComponent";
import { ButtonOpen } from "./ButtonOpenComponent";
import { validateFormData, InvalidFormDataError } from "../../../shared/validateFormData";
import { IFormData } from "../../../shared/FormModel/IFormData";
import { FormNotFoundError } from "../../../shared/FormRepository";
import { useHistory } from "react-router-dom";
import { APP_VERSION } from "../../constants";

/**
 * Component props
 */
export interface IMenuSidebarComponentProps {
	docMgr: DocumentManager;
}

/**
 * Menu sidebar
 *
 * @param props Component props
 */
export function MenuSidebar(props: IMenuSidebarComponentProps) {

	const history = useHistory();

	const stl = bem("menu-sidebar");

	const handleOpen = useMemo(() => async (files: FileList) => {

		const reader = new FileReader();
		let lastDocId = null;

		const readJSONFile = (file: File) => {

			return new Promise((resolve, reject) => {

				reader.onload = (ev) => {
					try {

						const data = JSON.parse(ev.target.result.toString()) as IFormData;

						try {

							validateFormData(data);
							data.id = null;

							lastDocId = props.docMgr.open(data.formId, data.formVersion, data);

							resolve(true);

						} catch (err) {

							if (err instanceof InvalidFormDataError) {
								console.error("Failed to load document:", err);
								alert(`Soubor '${file.name}' se nepodařilo načíst protože má neplatný formát nebo je poškozen.`);
							} else if (err instanceof FormNotFoundError) {
								console.error("Failed to load document:", err);
								alert(`Soubor '${file.name}' se nepodařilo načíst protože obsahuje nepodporovaný typ formuláře.`);
							} else {
								console.error("Failed to load document:", err);
								alert(`Soubor '${file.name}' se nepodařilo načíst kvůli neznámé chybě.`);
							}

							resolve(false);

						}

					} catch (err) {

						console.error("Failed to parse document file:", err);
						alert(`Soubor '${file.name}' se nepodařilo načíst, protože má neplatný formát nebo je poškozen.`);

						resolve(false);

					}
				};

				reader.onerror = (ev) => {

					console.error("Failed to open file:", ev);
					alert(`Soubor '${file.name}' se nepodařilo načíst.`);

					resolve(false);

				};

				reader.readAsText(file, "UTF-8");

			});

		};

		for (let i = 0; i < files.length; i++) {
			await readJSONFile(files[i]);
		}

		if (lastDocId) {
			history.push("/doc/" + lastDocId);
		}

	}, []);

	return <div className={stl.str}>
		<header className={stl.el("header").str}>
			<h1 className={stl.el("header-title").str}><strong>RONDO</strong> Formuláře</h1>
			<ButtonGroup flow="row">
				<Button
					label="Vytvořit"
					icon="plus"
					style="primary"
					link="/"
				/>
				<ButtonOpen
					label="Otevřít"
					icon="folder-open"
					style="primary"
					accept="application/json"
					onOpen={handleOpen}
				/>
			</ButtonGroup>
		</header>

		<div className={stl.el("content").str}>

			<MenuSection title="Otevřené dokumenty" grow={true}>
				<OpenedDocuments docMgr={props.docMgr} />
			</MenuSection>

		</div>

		<footer className={stl.el("footer").str}>
			<img src="/assets/media/kuchyne_rondo_big_grayscale.png" alt="Kuchyně rondo" />
			<p>Webové formuláře verze {APP_VERSION}</p>
		</footer>
	</div>;

}
